import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import type CK from '~/types/contentking';

import AccountDisplayName from '~/components/app/AccountDisplayName';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import useEffectiveHomeAccountId from '~/hooks/useEffectiveHomeAccountId';
import {
	useTeamWebsitesDropdownQuery,
} from './TeamWebsitesDropdown.gql';

import usePollInterval from '~/hooks/usePollInterval';



const messages = defineMessages({
	allAccounts: {
		id: 'ui.menu.allAccounts',
		defaultMessage: 'All accounts',
	},
	accountsOverview: {
		id: 'ui.menu.accountsOverview',
		defaultMessage: 'Accounts Overview',
	},
});



/**
 * Account List Section Component
 *
 * Displays a list of all available accounts as buttons.
 * Shown below AccountNavButton when user has multiple accounts and clicks AccountNavButton.
 */
type AccountListSectionProps = {
	onAccountSelect: (accountId: CK.AccountId) => void,
	onAccountSelectComplete?: () => void, // Callback after account selection (e.g., to reset hidden states)
	selectedAccountId: CK.AccountId | null,
};

const AccountListSection: React.FC<AccountListSectionProps> = (props) => {
	const {
		onAccountSelect,
		onAccountSelectComplete,
		selectedAccountId,
	} = props;
	const effectiveHomeAccountId = useEffectiveHomeAccountId();
	// Use selectedAccountId if provided, otherwise use effectiveHomeAccountId to determine active account
	const activeAccountId = selectedAccountId ?? effectiveHomeAccountId;

	const { data } = useTeamWebsitesDropdownQuery({
		pollInterval: usePollInterval(60000),
	});

	const accounts = data?.authenticatedSession?.accounts ?? [];

	// Don't render if no accounts
	if (accounts.length === 0) {
		return null;
	}

	return (
		<div className="main-nav__account-list-scrollable">
			<ul className="main-nav__account-list">
				{/* Title: "All accounts" - match main nav structure exactly */}
				<li
					className="main-nav__item main-nav__item--section-title"
					key="all-accounts-title"
				>
					<a
						className="main-nav__link"
						href="#"
						onClick={(e) => {
							e.preventDefault();
							// Title is not clickable, just for display
						}}
					>
						<MenuItemLayout
							icon={<></>}
							isActive={false}
							isDimmed={false}
							isLabelHidden={false}
							label={(
								<span className="account-list-section__title">
									<FormattedMessage {...messages.allAccounts} />
								</span>
							)}
						/>
					</a>
				</li>

				{/* List of account buttons - match main nav structure exactly */}
				{accounts.map((account) => {
					const isActive = account.id === activeAccountId;

					return (
						<li
							className="main-nav__item"
							data-account-id={account.id}
							data-account-list-item="true"
							key={`account-list-item-${account.id}`}
						>
							<a
								className="main-nav__link"
								href="#"
								onClick={(e) => {
									e.preventDefault();
									// Update selected account when clicked
									onAccountSelect(account.id);
									// Call completion callback if provided (e.g., to reset hidden states)
									if (onAccountSelectComplete) {
										onAccountSelectComplete();
									}
								}}
							>
								<MenuItemLayout
									icon={<></>}
									isActive={isActive}
									isDimmed={false}
									isLabelHidden={false}
									label={(
										<AccountDisplayName accountId={account.id} />
									)}
								/>
							</a>
						</li>
					);
				})}
			</ul>

			{/* Divider + Accounts Overview - sticky at bottom when scrolling (always visible) */}
			<div className="main-nav__account-list-sticky-footer">
				<div
					className="main-nav__divider"
					data-account-list-divider="true"
				/>
				<div
					className="main-nav__item"
					data-account-list-item="true"
					data-account-list-overview="true"
				>
					<a
						className="main-nav__link"
						href="#"
						onClick={(e) => {
							e.preventDefault();
							// Placeholder - functionality will be added later
						}}
					>
						<MenuItemLayout
							icon={<></>}
							isActive={false}
							isDimmed={false}
							isLabelHidden={false}
							label={(
								<FormattedMessage {...messages.accountsOverview} />
							)}
						/>
					</a>
				</div>
			</div>
		</div>
	);
};



export default AccountListSection;
